/**
 * Pure-data English catalogue for namespace 'cmdMotion' (W1-10b core-commands-motion; imports only core/util/types).
 * Failure reasons and toast notes of the motion commands (keys, segments, layers, links, transforms, clipboard).
 * Keys are written WITHOUT the namespace prefix; the commands produce MessageRefs such as
 * 'cmdMotion.firstKeyDelete' (constants in motionTrack.ts `MOTION_MSG`).
 */

import type { MessageCatalog } from '../util/types';

const catalog: MessageCatalog = {
  ns: 'cmdMotion',
  locale: 'en',
  messages: {
    // Missing or stale targets
    trackMissing: 'This motion no longer exists. It may have been removed by another change.',
    keyMissing: 'The selected keyframes no longer exist.',
    lineMissing: 'This line no longer exists.',
    noKeys: 'This motion has no keyframes.',
    noTrack: 'This property is not animated on this line.',
    nothingAnimated: 'Nothing on this line is animated yet. Add a keyframe to a property first.',

    // Track and key rules
    keysCollide: 'Two keyframes would land on the same beat. Leave at least one grid step between them.',
    beforeZero: 'Keyframes and notes cannot move before beat 0.',
    valueType:
      'This value does not fit this property ({type, select, number {a number} rgb {a colour} string {a text} vec2 {a pair of numbers} rgba8 {a colour with opacity} other {a value of its type}} is needed).',
    valueNotFinite: 'Enter a finite number.',
    layerOnExtended: 'Only position, rotation, opacity and scroll speed have layers.',
    layerInvalid: 'Layers are numbered from 0.',
    layerLimit: 'A property can have at most {max} layers.',
    notLayered: 'Only position, rotation, opacity and scroll speed have layers.',
    gifNeedsGifTexture: 'GIF progress needs a GIF image on this line.',
    varMissing: 'This effect or video value no longer exists.',

    // Deleting and jumps
    firstKeyDelete:
      'The first keyframe cannot be deleted while others remain. Use Reset property to remove the whole motion.',
    firstKeyJump: 'The first keyframe cannot jump: nothing arrives at it.',
    gifTrailingJump: 'The last keyframe of a GIF cannot jump: the GIF would stop playing.',
    arriveAfterHold:
      'After a Stay segment the arriving value is the stayed value. Change the Stay keyframe instead, or turn off Stay.',
    jumpKeptAfterHold:
      '{count, plural, one {# jump was} other {# jumps were}} kept: after a Stay segment the arriving value is the stayed value.',

    // Easing
    noSegments: 'Select one or more segments (the curve between two keyframes).',
    easeSkipped:
      '{count, plural, one {# segment was} other {# segments were}} left unchanged because this curve is not allowed there.',

    // Splits
    splitOutside: 'Split needs a beat inside a segment, between two keyframes.',
    splitApprox: 'Curve split approximately (within {percent} % of its range). Bake it for an exact result.',
    splitApproxText:
      '{ms, plural, =0 {Text split: Phira now counts the shown characters separately in each half. Here every character still appears at the same moment.} other {Text split approximately: Phira counts the shown characters differently in each half, so some appear up to {ms} ms earlier or later. Keep typing segments whole when exact timing matters.}}',
    splitApproxColor: 'Colour split approximately: the new keyframe was rounded to whole colour values.',
    splitBaked:
      'Part of this curve cannot be split exactly in Phira, so it was turned into short straight pieces that follow it (within {percent} % of its range).',
    splitBakedText:
      '{ms, plural, =0 {Part of this text curve cannot be split exactly in Phira, so it was turned into short pieces. Every character still appears at the same moment.} other {Part of this text curve cannot be split exactly in Phira, so it was turned into short pieces. Characters may appear up to {ms} ms earlier or later.}}',
    splitLeadIn:
      'Before its first keyframe this motion continues its first curve backwards. The new keyframe starts a straight segment there, which changes that start by up to {percent} % of its range. Use Hold the first value for a steady start.',
    splitApproxGif: 'The GIF played on its own at its natural speed here. This keyframe now controls that part of it.',
    splitLinearHalf:
      'The curve next to this keyframe cannot keep its shape with the new value, so that part became a straight line.',
    splitPartner:
      'Position X and Y are linked, so the other axis got a keyframe at this beat too. {kind, select, bake {Part of its curve was turned into short straight pieces} leadIn {Its start before the first keyframe changed} other {Its curve was split approximately}} (within {percent} % of its range).',

    // Bake, join, extrapolate
    bakeDivInvalid: 'The step must be a whole fraction of a beat (1 to 30720 pieces per beat).',
    bakeTooMany: 'Baking would create more than {max} pieces. Choose a coarser step or fewer segments.',
    joinNotExact: 'These two segments cannot be joined into one exact curve. (Fit is planned for a later version.)',
    joinNeedsMiddle: 'Join needs a keyframe between two segments.',
    joinPartner: 'Position X and Y are linked, and the other axis cannot be joined exactly at this beat.',
    extrapolateNeedsTwo: 'Extrapolate needs two segments before the selected one.',
    extrapolateSkipped:
      '{count, plural, one {# segment was} other {# segments were}} skipped: extrapolation needs two numeric segments before it.',

    // Links
    linkNeedsTwo: 'Select at least two segments to link.',
    linkPrimary: 'The segment to copy from must be one of the selected segments.',
    linkValueType: 'Only segments of the same kind of value can be linked (numbers, colours or text).',

    // Expressions
    exprNoNumbers: 'Expressions work on numeric keyframes only.',
    exprSkipped:
      '{count, plural, one {# keyframe was} other {# keyframes were}} left unchanged: the expression gave no valid number there.',

    // Lead-in, raw events
    leadInAlready: 'This motion already starts at beat 0.',
    replaceEmpty: 'An empty event list cannot be written. Use Reset property to remove the motion.',
    replaceOrder: 'Events must be in time order without overlaps.',
    replaceValue:
      'An event value does not fit this property ({type, select, number {a number} rgb {a colour} string {a text} vec2 {a pair of numbers} rgba8 {a colour with opacity} other {a value of its type}} is needed).',
    replaceEase: 'An event uses a curve this property cannot have.',

    // Layers
    moveNotLayered: 'Only position, rotation, opacity and scroll speed segments can move to another layer.',
    nothingToMove: 'The selected segments are already on that layer.',
    noFreeLayer: 'There is no free layer left for this property (at most 4).',

    // Value shortcuts
    negateNothing: 'Negate works on position, rotation and scroll speed keyframes; on opacity it sets 0 %.',
    negateSkipped:
      '{count, plural, one {# keyframe was} other {# keyframes were}} left unchanged: only position, rotation, speed and opacity can be negated.',
    opacityNothing: 'Full opacity works on opacity keyframes only.',
    opacitySkipped: '{count, plural, one {# keyframe was} other {# keyframes were}} left unchanged: not opacity.',

    // Transforms and time
    timeScaleInvalid: 'The time scale must be a fraction of two positive whole numbers, such as 2/1 or 1/2.',
    transformCollide: 'Keyframes would pass keyframes that are not selected. Select them too, or use a smaller change.',
    transformNothing: 'Select notes or keyframes to transform.',
    timeLengthInvalid: 'The length to insert must be more than 0 beats.',
    timeRangeInvalid: 'Choose a range that starts at or after beat 0 and ends after it starts.',
    timeLeadIn:
      'Before its first keyframe this motion continues its first curve backwards. That curve moved in time, which changes the start by up to {percent} % of its range. Use Hold the first value for a steady start.',
    timeShrunk:
      '{count, plural, one {# curve had} other {# curves had}} no keyframe inside the removed range, so {count, plural, one {it was} other {they were}} shortened as a whole, the reverse of Insert time. To cut the range out instead, add keyframes at both edges first.',
    reverseNothing: 'Select notes or segments to reverse.',

    // Clipboard
    pasteNothing: 'Nothing to paste here.',
    pasteRepeatInvalid: 'Repeat needs a whole number of copies and a spacing of more than 0 beats.',
    pasteUnmatched:
      '{count, plural, one {# value had} other {# values had}} no keyframe at the same beat to add to. Paste the rest as a new layer.',
    pasteSkipped:
      '{count, plural, one {# item was} other {# items were}} not pasted: the line is missing, the property does not apply to this line, its curve is not allowed there, or it would land before beat 0.',
    pasteNotesOnUiMover:
      '{count, plural, one {# note was} other {# notes were}} not pasted: notes on a UI mover line would be invisible.',
    pasteHitsoundsDropped:
      '{count, plural, one {# custom hitsound is} other {# custom hitsounds are}} not in this project; those notes use the default sound.',
    pasteLinesNothing: 'The clipboard holds no lines. Copy lines from the Lines panel first.',
    pasteLinesMissingAssets:
      '{count, plural, one {# file is} other {# files are}} missing from this project. Lines and notes that used them show the default look.',
  },
};

export default catalog;
